import type { UserDto } from "@stylingcv/dto";
import { authResponseSchema } from "@stylingcv/dto";
import type { LoaderFunction } from "react-router-dom";
import { redirect } from "react-router-dom";

import { USER_KEY } from "@/client/constants/query-keys";
import { queryClient } from "@/client/libs/query-client";
import { fetchUser } from "@/client/services/user";
import { useAuthStore } from "@/client/stores/auth";
import { useTokenStore } from "@/client/stores/token";
import { trackLogin, trackSignUp } from "@/client/utils/gtm";
import { addLocaleToPath } from "@/client/utils/url-locales";

const LOCAL_STORAGE_KEY = "guest-resume";

/** Build a locale-aware redirect path using stored locale preference */
function localizedRedirect(path: string): ReturnType<typeof redirect> {
  const storedLocale = typeof window !== "undefined" ? window.localStorage.getItem("locale") : null;
  const target = storedLocale ? addLocaleToPath(path, storedLocale) : path;
  return redirect(target);
}

export const authLoader: LoaderFunction<UserDto> = async ({ request }) => {
  const url = new URL(request.url);
  const status = url.searchParams.get("status");
  const provider = url.searchParams.get("provider");

  console.log(`[AuthLoader] Processing auth callback. Status: ${status}, Provider: ${provider}`);

  // Handle explicit error status from server (OAuth failure)
  if (status === "error") {
    const message = url.searchParams.get("message");
    console.error(`[AuthLoader] OAuth error from ${provider || "unknown"}: ${message || "Unknown error"}`);
    return localizedRedirect(`/auth/login?error=oauth&provider=${provider || ""}`);
  }

  const { success } = authResponseSchema.pick({ status: true }).safeParse({ status });

  if (!success) {
    console.warn(`[AuthLoader] Invalid status or parse failure for: ${status}, redirecting to login`);
    return localizedRedirect("/auth/login");
  }

  if (status === "2fa_required") {
    return localizedRedirect("/auth/verify-otp");
  }

  const user = await queryClient.fetchQuery({
    queryKey: [USER_KEY],
    queryFn: fetchUser,
  });

  if (!user) {
    console.error("[AuthCallback] fetchUser returned null — cookies may not have been set");
    return localizedRedirect("/auth/login?error=oauth");
  }

  if (status === "authenticated") {
    useAuthStore.setState({ user });
    useTokenStore.getState().reset();

    // Track OAuth login/signup
    // Distinguish new signups from returning logins by createdAt freshness.
    // OAuth callbacks don't return an explicit isNewUser flag, so we infer
    // from User.createdAt within a 60-second tolerance.
    const NEW_USER_WINDOW_MS = 60_000;
    const isNewUser = (() => {
      try {
        if (!user.createdAt) return false;
        const createdMs = new Date(user.createdAt).getTime();
        return Number.isFinite(createdMs) && Date.now() - createdMs < NEW_USER_WINDOW_MS;
      } catch {
        return false;
      }
    })();
    if (isNewUser) {
      trackSignUp({ method: provider || "oauth" });
    } else {
      trackLogin({ method: provider || "oauth" });
    }

    // Check if we have guest resume data to import after login
    try {
      const guestResumeData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (guestResumeData) {
        // We'll keep the guest resume data in localStorage until the user either
        // imports it or navigates to a different page
        // The actual import will be handled in the dashboard with a prompt
      }
    } catch (error) {
      console.error("Failed to process guest resume data:", error);
    }

    // Use user's stored locale (from profile) if available, otherwise fall back to localStorage
    const userLocale = user.locale;
    if (userLocale) {
      return redirect(addLocaleToPath("/dashboard", userLocale));
    }
    return localizedRedirect("/dashboard");
  }
};
